import React from "react";

import * as yup from "yup";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import { Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { ValidationError } from "components";
import { useFormik } from "formik";
import { ApiValidationErrors, IEasyLoginResponse } from "store/types";

interface AuthCodeProps {
  data?: IEasyLoginResponse;
  resendCode: () => void;
  onSubmit: (login_code: string) => Promise<void>;
  goBack: () => void;
  apiValidationErrors?: { errors: ApiValidationErrors };
  loading: boolean;
}

const schema = yup.object().shape({
  code: yup.string().required()
});

const getLoginWay = (data: IEasyLoginResponse | {}): string => {
  if ("phone_number" in data) {
    return data.phone_number;
  }

  if ("email" in data) {
    return data.email;
  }

  return "your phone or email";
};

export const LoginWithCode: React.FC<AuthCodeProps> = ({
  goBack,
  data = {},
  resendCode,
  onSubmit,
  apiValidationErrors,
  loading
}) => {
  const { values, handleChange, isValid, handleSubmit, errors } = useFormik({
    validationSchema: schema,
    initialValues: { code: "" },
    onSubmit: (values) => onSubmit(values.code)
  });

  return (
    <Stack alignItems="stretch" gap="50px">
      <Typography
        sx={{
          color: "#173c4c",
          fontFamily: "Oswald, sans-serif",
          fontSize: "2.5rem",
          fontWeight: 300,
          letterSpacing: "1.25px",
          lineHeight: "60px"
        }}
      >
        Altius Login with Verification Code
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack alignItems="stretch" gap="20px" maxWidth="300px">
          <Typography
            sx={{
              color: "#173c4c",
              textAlign: "center"
            }}
          >
            We sent a message with a temporary code to {getLoginWay(data)}
          </Typography>
          <TextField
            name="code"
            placeholder="Enter the code"
            value={values.code}
            autoFocus
            error={!!errors?.code}
            helperText={errors?.code}
            onChange={handleChange}
          />
          {apiValidationErrors?.errors?.login && <ValidationError messages={apiValidationErrors.errors.login} />}
          <Button
            disabled={!isValid || !values.code}
            variant="contained"
            type="submit"
            sx={{
              borderRadius: "5px",
              textTransform: "none"
            }}
          >
            Login
          </Button>
          <Stack
            direction="row"
            sx={{
              fontSize: "0.8rem",
              textAlign: "center",
              color: "#173c4c",
              alignItems: "center",
              gap: "5px",
              justifyContent: "center",
              cursor: "pointer"
            }}
            onClick={resendCode}
          >
            Didn’t receive the code?
            <Typography className="scale-click" sx={{ color: "#26a6ae", fontSize: "0.8rem" }}>
              Click to resend
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ gap: "8px", justifyContent: "center", cursor: "pointer" }} onClick={goBack}>
            <ArrowCircleDownIcon sx={{ transform: "rotate(90deg)" }} />
            <Typography sx={{ color: "#173c4c" }}>Go back to main login</Typography>
          </Stack>
          {loading && (
            <Stack direction="row" justifyContent="center" mt="50px">
              <CircularProgress size={100} sx={{ color: "#173c4c" }} />
            </Stack>
          )}
        </Stack>
      </form>
    </Stack>
  );
};
